import axios from 'axios';
import { globalConfig } from '../configuration/config';

let isSyncInProgress = false;

function serializeSubscription(subscription) {
  try {
    const json = subscription?.toJSON ? subscription.toJSON() : subscription;
    const endpoint = json?.endpoint || subscription?.endpoint || '';
    const p256dh = json?.keys?.p256dh || (subscription?.getKey && subscription.getKey('p256dh')) || '';
    const auth = json?.keys?.auth || (subscription?.getKey && subscription.getKey('auth')) || '';
    return {
      endpoint,
      keys: {
        p256dh: typeof p256dh === 'string' ? p256dh : btoa(String.fromCharCode.apply(null, new Uint8Array(p256dh || []))),
        auth: typeof auth === 'string' ? auth : btoa(String.fromCharCode.apply(null, new Uint8Array(auth || []))),
      },
      expirationTime: json?.expirationTime ?? null,
    };
  } catch (e) {
    return subscription;
  }
}

async function postSubscriptionToServer(subscription) {
  try {
    const payload = serializeSubscription(subscription);
    const endpoint = payload?.endpoint || '';
    const lastSentEndpoint = localStorage.getItem('push_endpoint') || '';
    if (endpoint && endpoint === lastSentEndpoint) {
      return; // already synced
    }
    if (isSyncInProgress) {
      return; // coalesce parallel attempts
    }
    isSyncInProgress = true;
    const response = await axios.post(
      `${globalConfig.config.common.api}PushSubscription/AddPushSubscription`,
      payload,
      { withCredentials: true },
    );
    localStorage.setItem('push_endpoint', endpoint);
    return response;
  } catch (error) {
    throw error;
  } finally {
    isSyncInProgress = false;
  }
}
const VAPID_PUBLIC_KEY =
  'BGB_2fBlhhg97HD5z4tVW6OmSxrPC6mdP_awyukS_64pJ0ua2dRa0uicv8BpAHsO220Q-RyP_Pw5fa2lt3WyHLs';

// Register the service worker
export async function registerServiceWorker() {
  try {
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      const registration = await navigator.serviceWorker.register('/service-worker.js');
      console.log('Service Worker registered:', registration);
      updateUI();
    } else {
      console.error('Service Worker or Push is not supported in this browser.');
    }
  } catch (error) {
    console.error('Service Worker registration failed:', error);
  }
}

// Make sure you check registration status before calling getRegistration()
export async function subscribeToPush() {
  try {
    // Ensure an ACTIVE registration
    const registration = await navigator.serviceWorker.ready;
    if (!registration || !('pushManager' in registration)) {
      console.error('Service Worker registration not found or PushManager not available.');
      return false;
    }

    // Permission must be granted before subscribing
    if (Notification.permission !== 'granted') {
      console.warn('Notification permission is not granted; skip subscribeToPush');
      return false;
    }

    let subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlB64ToUint8Array(VAPID_PUBLIC_KEY),
      });
      console.log('Created new subscription:', subscription);
    } else {
      console.log('Using existing subscription:', subscription);
    }
    try {
      const response = await postSubscriptionToServer(subscription);
      console.log('Subscription synced with server:', response?.data, 'endpoint:', (subscription?.endpoint||'').slice(-10));
      return true;
    } catch (error) {
      console.error('Error posting subscription to server:', error);
      return false;
    }

    updateUI();
  } catch (error) {
    console.error('Subscription to push failed:', error);
    return false;
  }
}

// Ensure server has the current subscription even if it already exists locally
export async function syncSubscriptionWithServer() {
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) return;
    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) return;
    await postSubscriptionToServer(subscription);
  } catch (e) {
    console.warn('syncSubscriptionWithServer failed:', e);
  }
}

// Request permission from a user gesture and then subscribe
export async function requestPermissionThenSubscribe() {
  try {
    const current = Notification.permission;
    if (current === 'granted') {
      return await subscribeToPush();
    }
    const perm = await Notification.requestPermission();
    if (perm === 'granted') {
      return await subscribeToPush();
    } else {
      console.warn('Notifications permission was not granted');
      return false;
    }
  } catch (error) {
    console.error('Permission/subscribe flow failed:', error);
    return false;
  }
}

// Unsubscribe from push notifications
// Unsubscribe from push notifications
export async function unsubscribeFromPush() {
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) {
      console.error('Service Worker registration not found');
      return false;
    }

    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      const endpoint = encodeURIComponent(encodeURIComponent(subscription.endpoint));
      await subscription.unsubscribe();
      console.log('Unsubscribed from push:', subscription);

      // Use DELETE method to remove the subscription on the server
      const deleteUrl = `${globalConfig.config.common.api}PushSubscription/RemovePushSubscription/${endpoint}`;
      await fetch(deleteUrl, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      console.log('Subscription removed from server:', endpoint);
      localStorage.removeItem('push_endpoint');
      //   updateUI();
      return true;
    } else {
      console.log('No subscription found');
      return true;
    }
  } catch (error) {
    console.error('Unsubscription from push failed:', error);
    return false;
  }
}

// Notify the server for push notifications
export async function notifyMe() {
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) {
      console.error('Service Worker registration not found');
      return;
    }

    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      console.log('Notify server about push notification');
      postToServer('/notify-me', { endpoint: subscription.endpoint });
    } else {
      console.error('No subscription found');
    }
  } catch (error) {
    console.error('Error notifying server:', error);
  }
}

// Update the UI based on the push subscription status
// Update the UI based on the push subscription status
function updateUI() {
  const registrationButton = document.getElementById('register');
  const unregistrationButton = document.getElementById('unregister');
  const subscriptionButton = document.getElementById('subscribe');
  const unsubscriptionButton = document.getElementById('unsubscribe');
  const notifyMeButton = document.getElementById('notify-me');

  // Check if the buttons exist before trying to modify them
  if (registrationButton) registrationButton.disabled = true;
  if (unregistrationButton) unregistrationButton.disabled = true;
  if (subscriptionButton) subscriptionButton.disabled = true;
  if (unsubscriptionButton) unsubscriptionButton.disabled = true;
  if (notifyMeButton) notifyMeButton.disabled = true;

  navigator.serviceWorker
    .getRegistration()
    .then((registration) => {
      if (!registration) {
        if (registrationButton) registrationButton.disabled = false;
      } else {
        if (unregistrationButton) unregistrationButton.disabled = false;

        // Handle pushManager subscription correctly
        registration.pushManager
          .getSubscription()
          .then((subscription) => {
            if (!subscription) {
              if (subscriptionButton) subscriptionButton.disabled = false;
            } else {
              if (unsubscriptionButton) unsubscriptionButton.disabled = false;
              if (notifyMeButton) notifyMeButton.disabled = false;
            }
          })
          .catch((error) => {
            console.error('Error getting push subscription:', error);
          });
      }
    })
    .catch((error) => {
      console.error('Error getting service worker registration:', error);
    });
}

// Utility function to convert Base64 to Uint8Array
const urlB64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

// Post the data to the server
const postToServer = async (url, data) => {
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Network response was not ok');
    }

    console.log('Data successfully posted to server:', data);
  } catch (error) {
    console.error('Error posting data to server:', error);
  }
};
